import React, { useMemo, useState } from 'react';
import { OAuthLogins } from '../OAuthLogins';
import { EmailLoginButton } from '../Buttons';
import emailIsValid from '@repo/utils/helpers/valid-email';
import TextInput from '../TextInput';
import styles from './AuthOptions.module.scss';
import { apiRequest } from '@repo/utils/helpers/api';
import cn from 'classnames';
import { setAppStartRoute } from '@repo/utils/helpers/app-start-route';
import { useLocation } from 'react-router-dom';
import analytics from '@repo/utils/helpers/analytics';
import { previousAuthEmail, storeLastAuthEmail } from '@repo/utils/helpers/jwt';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { LastUsedTag } from '@repo/components/buttons';

export const WordSeparator = ({
  className,
  word = 'or',
}: {
  className?: string;
  word?: string;
}) => <div className={cn(styles.or, className)}>{word}</div>;

export default function AuthOptions({
  signingInWith,
  setSigningInWith,
  lastProvider,
  userHasExistingAccount,
  setLoginEmailSent,
  setLoginEmailRecipient,
  autoFocusEmailInput,
  beforeAuthOptions,
}) {
  const { pathname } = useLocation();
  const lastLoginEmail = useMemo(() => previousAuthEmail(), []);
  const haveEmailForLastLogin =
    userHasExistingAccount && lastProvider === 'email' && lastLoginEmail;
  const [userEmail, setUserEmail] = useState(haveEmailForLastLogin ? lastLoginEmail : '');

  const loginWithEmail = async () => {
    setSigningInWith('email');
    storeLastAuthEmail(userEmail);

    try {
      const login = await apiRequest<{ email: string }>(
        'POST',
        userHasExistingAccount ? '/auth/email-login/' : '/auth/email-signup/',
        { email: userEmail },
        { apiVersion: 'v2', noAuthorization: true },
      );

      if (pathname !== '/login') {
        setAppStartRoute(pathname);
      }

      // handle signups from birthday
      if (!!localStorage.getItem('signup_from_birthday')) {
        localStorage.removeItem('signup_from_birthday');
        analytics.track('Sign-up started from birthday submission');
      }

      setLoginEmailRecipient(login.email);
      setLoginEmailSent(true);
      setUserEmail('');
      setSigningInWith(null);
    } catch (e) {
      console.error(e);
      setSigningInWith(null);
    }
  };

  return (
    <>
      <StateBasedFadeTransition
        state={String(userHasExistingAccount)}
        elementByStateMap={{
          true: <div className={cn(styles.copy, 'hasAccount')}>{beforeAuthOptions}</div>,
          false: <div className={cn(styles.copy, 'noAccount')}>{beforeAuthOptions}</div>,
        }}
        duration="slow"
      />

      <div className={styles.authOptions}>
        <OAuthLogins
          userHasExistingAccount={userHasExistingAccount}
          signingInWith={signingInWith}
          setSigningInWith={setSigningInWith}
          lastProvider={lastProvider}
        />
        <WordSeparator />
        <div className={styles.emailInput}>
          <TextInput
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            onEnter={loginWithEmail}
            placeholder={haveEmailForLastLogin ? `${lastLoginEmail}` : 'Enter your email'}
            autoFocus={autoFocusEmailInput || haveEmailForLastLogin}
            validateValue={emailIsValid}
            invalidValueMsg="Please enter a valid email"
            maxLength={128}
          />
          {haveEmailForLastLogin && <LastUsedTag />}
        </div>
        <EmailLoginButton
          className={styles.emailLoginBtn}
          onClick={loginWithEmail}
          isSignUp={!userHasExistingAccount}
          inProgress={signingInWith === 'email'}
          disabled={!emailIsValid(userEmail)}
        />
      </div>
    </>
  );
}
